import React, { useEffect, useMemo, useState } from 'react';
import { ChartBarIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { usePonds, useMasterData } from '../../hooks/useApi';
import apiClient from '../../utils/api';

const FeedTrends: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [selectedPondId, setSelectedPondId] = useState<string>('');
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [threshold, setThreshold] = useState<string>('1.5');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [rows, setRows] = useState<any[]>([]);
  const [rangeFcr, setRangeFcr] = useState<number | null>(null);

  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const pondOptions = useMemo(() => ponds || [], [ponds]);
  const { data: feedBrands } = useMasterData('feed_brands', selectedLocation?.id || null);
  const brandOptions = useMemo(() => feedBrands || [], [feedBrands]);

  useEffect(() => {
    // default last 30 days
    const today = new Date();
    const to = today.toISOString().slice(0, 10);
    const from = new Date(today.getTime() - 29 * 86400000).toISOString().slice(0, 10);
    setStart(from);
    setEnd(to);
  }, []);

  const fetchData = async () => {
    if (!selectedLocation || !start || !end) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/reports/feed-trends', {
        params: {
          location_id: selectedLocation.id,
          start,
          end,
          pond_id: selectedPondId || undefined,
          brand: selectedBrand || undefined,
          threshold: parseFloat(threshold) || 1.5,
        },
      });
      setRows(res.data?.rows || []);
      setRangeFcr(res.data?.range_fcr ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load feed trends');
      setRows([]);
      setRangeFcr(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    if (!selectedLocation || !start || !end) return;
    try {
      const res = await apiClient.get(`/reports/export/feed-trends`, {
        params: {
          location_id: selectedLocation.id,
          start,
          end,
          pond_id: selectedPondId || undefined,
          brand: selectedBrand || undefined,
        },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `feed_trends_${start}_${end}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {}
  };

  // Prepare chart data
  const chartPoints = useMemo(() => {
    if (!rows || rows.length === 0) return [] as { date: string; total: number; ma: number | null }[];
    // When "All ponds" is selected, aggregate totals per date across ponds and compute MA here
    if (!selectedPondId) {
      const byDate: Record<string, number> = {};
      rows.forEach((r: any) => {
        byDate[r.date] = (byDate[r.date] || 0) + Number(r.total_feed_kg || 0);
      });
      const dates = Object.keys(byDate).sort();
      const pts: { date: string; total: number; ma: number | null }[] = dates.map((d) => ({ date: d, total: Number(byDate[d] || 0), ma: null }));
      // compute 30d MA over aggregated sequence
      const window: number[] = [];
      let sum = 0;
      pts.forEach((p, i) => {
        if (window.length === 30) { sum -= window.shift() as number; }
        // MA excludes today -> set ma using current window, then push today
        p.ma = window.length > 0 ? Number((sum / window.length).toFixed(6)) : null;
        window.push(p.total); sum += p.total;
      });
      return pts;
    }
    // Single-pond view: use backend-provided totals and MA
    return rows
      .filter((r: any) => r.pond_id === selectedPondId)
      .map((r: any) => ({ date: r.date, total: Number(r.total_feed_kg || 0), ma: r.ma_30d != null ? Number(r.ma_30d) : null }));
  }, [rows, selectedPondId]);

  const chartPaths = useMemo(() => {
    if (chartPoints.length === 0) return { totalPath: '', maPath: '' };
    const paddingLeft = 40; // for y-axis labels
    const paddingRight = 10;
    const paddingTop = 10;
    const paddingBottom = 24; // for x-axis labels
    const w = Math.max(360, chartPoints.length * 28);
    const h = 200;
    // Robust y-scale: pad and avoid extreme distortion by using max of totals and MA
    const vals = [
      ...chartPoints.map((p) => p.total),
      ...chartPoints.filter((p) => p.ma != null).map((p) => p.ma as number)
    ];
    const minRaw = Math.min(...vals, 0);
    const maxRaw = Math.max(...vals, 1);
    const minY = Math.min(0, Math.floor(minRaw));
    const maxY = Math.ceil(maxRaw * 1.1); // add 10% headroom
    const x = (i: number) => paddingLeft + (i * (w - paddingLeft - paddingRight)) / Math.max(1, chartPoints.length - 1);
    const y = (v: number) => h - paddingBottom - ((v - minY) / Math.max(1e-9, maxY - minY)) * (h - paddingTop - paddingBottom);
    const totalPath = chartPoints
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.total)}`)
      .join(' ');
    const maPath = chartPoints
      .filter((p) => p.ma != null)
      .map((p, i) => {
        const idx = chartPoints.indexOf(p);
        return `${i === 0 ? 'M' : 'L'} ${x(idx)} ${y(p.ma as number)}`;
      })
      .join(' ');
    // y-axis ticks (5 ticks)
    const ticks: { y: number; value: number }[] = [];
    for (let i = 0; i <= 5; i++) {
      const v = minY + ((maxY - minY) * i) / 5;
      ticks.push({ y: y(v), value: Number(v.toFixed(1)) });
    }
    // x-axis labels (first, middle, last)
    const xLabels: { x: number; label: string }[] = [];
    if (chartPoints.length > 0) {
      const first = { x: x(0), label: chartPoints[0].date.slice(5) };
      const midIdx = Math.floor(chartPoints.length / 2);
      const mid = { x: x(midIdx), label: chartPoints[midIdx].date.slice(5) };
      const last = { x: x(chartPoints.length - 1), label: chartPoints[chartPoints.length - 1].date.slice(5) };
      xLabels.push(first, mid, last);
    }
    return { totalPath, maPath, w, h, paddingLeft, paddingRight, paddingTop, paddingBottom, ticks, xLabels } as any;
  }, [chartPoints]);

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="flex items-center gap-2">
        <ChartBarIcon className="h-6 w-6 text-green-600" />
        <h1 className="text-2xl font-bold text-gray-900">Feed Trends (with FCR)</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pond</label>
            <select value={selectedPondId} onChange={(e) => setSelectedPondId(e.target.value)} className="w-full px-3 py-2 border rounded">
              <option value="">All ponds</option>
              {pondOptions.map((p: any) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
            <select value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)} className="w-full px-3 py-2 border rounded">
              <option value="">All</option>
              {brandOptions.map((b: any) => (
                <option key={b.id} value={b.name}>{b.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Spike Threshold</label>
            <input type="number" step="0.1" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-full px-3 py-2 border rounded" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Apply Filters</button>
          <button onClick={downloadCsv} className="flex items-center px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700">
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" /> CSV
          </button>
        </div>

        {error && (
          <div className="mt-4 text-sm text-red-600">{error}</div>
        )}

        <div className="mt-4">
          <div className="text-sm text-gray-700">FCR (range): {rangeFcr !== null ? rangeFcr : 'N/A'}</div>
        </div>

        <div className="mt-2 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Pond</th>
                <th className="text-left px-3 py-2">Total Feed (kg)</th>
                <th className="text-left px-3 py-2">MA (30d)</th>
                <th className="text-left px-3 py-2">Spike</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-3 py-3">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-3 text-gray-500">No data for selected range</td></tr>
              ) : (
                rows.map((r: any, idx: number) => (
                  <tr key={idx} className="border-t">
                    <td className="px-3 py-2">{r.date}</td>
                    <td className="px-3 py-2">{r.pond_name}</td>
                    <td className="px-3 py-2">{r.total_feed_kg}</td>
                    <td className="px-3 py-2">{r.ma_30d ?? '—'}</td>
                    <td className="px-3 py-2">{r.spike ? '⚠️' : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FeedTrends;


